/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} MemberPriceRefs
 * @property {HTMLElement} memberPrice
 */

/**
 * Keeps the Floyd Friends member price in step with the selected variant.
 *
 * Deliberately does no arithmetic. On a variant change the theme re-renders the section
 * server-side, so the incoming HTML already carries the member price that the block's
 * Liquid computed for the new variant. Swapping it in wholesale is the only way to
 * guarantee the displayed figure and the cart figure stay derived from the same code.
 *
 * Mirrors the pattern in product-price.js — same event, same block-id matching.
 *
 * @extends {Component<MemberPriceRefs>}
 */
class MemberPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Replaces the rendered price when a different variant is selected.
   * @param {any} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newMemberPrice = html.querySelector(`member-price[data-block-id="${this.dataset.blockId}"]`);

        // The new variant may carry no member price at all, in which case the block renders
        // nothing. Removing our own markup is the correct response — leaving the label up
        // would name a members' price next to a figure that is not one.
        if (!newMemberPrice) {
          this.refs.memberPrice?.remove();
          return;
        }

        const newRow = newMemberPrice.querySelector('[ref="memberPrice"]');
        if (!newRow) return;

        if (this.refs.memberPrice) {
          this.refs.memberPrice.replaceWith(newRow);
        } else {
          this.append(newRow);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[member-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('member-price')) {
  customElements.define('member-price', MemberPrice);
}
